import type { MailingChannel, ScenarioStep } from './mailingDomain';
import i18next from '../i18n/initI18n';

/**
 * Каналы каскадных шаблонов (порядок = перестановки и фильтры на вкладке шаблонов).
 * Сначала доступные SMS / Email / Viber, затем push и WABA (в разработке).
 */
export const TEMPLATE_CHANNEL_ORDER: MailingChannel[] = ['sms', 'email', 'viber', 'push', 'waba'];
export const TEMPLATE_DISABLED_CHANNELS: ReadonlySet<MailingChannel> = new Set(['push', 'waba']);

/** Все упорядоченные наборы из `len` разных каналов (перестановки P(order.length, len)). */
export function distinctChannelTuples(channels: MailingChannel[], len: number): MailingChannel[][] {
  if (len <= 0) return [[]];
  if (len === 1) return channels.map((c) => [c]);
  const out: MailingChannel[][] = [];
  for (const head of channels) {
    const rest = channels.filter((c) => c !== head);
    for (const tail of distinctChannelTuples(rest, len - 1)) {
      out.push([head, ...tail]);
    }
  }
  return out;
}

export function defaultScenarioSteps(channels: MailingChannel[]): ScenarioStep[] {
  return channels.map((channel, i) => ({
    channel,
    order: i,
    delayMinutes: 30,
    timeoutMinutes: 5,
  }));
}

export type CascadeTemplateDef = {
  /** Стабильный ключ для React */
  key: string;
  channels: MailingChannel[];
  title: string;
  description: string;
  inDevelopment: boolean;
  disabledReason?: string;
};

function channelTitle(c: MailingChannel): string {
  return i18next.t(`channels.${c}`);
}

export function channelChainLabel(channels: MailingChannel[]): string {
  return channels.map((c) => channelTitle(c)).join(' → ');
}

function templateDisabledReason(channels: MailingChannel[]): string | undefined {
  const disabled = channels.filter((ch) => TEMPLATE_DISABLED_CHANNELS.has(ch));
  if (!disabled.length) return undefined;
  return i18next.t('campaignsPage.templateInDevelopmentReason', {
    channels: disabled.map((ch) => channelTitle(ch)).join(', '),
  });
}

/** Все шаблоны: перестановки 2–5 каналов из полного набора. */
export function buildAllCascadeTemplates(): CascadeTemplateDef[] {
  const chs = TEMPLATE_CHANNEL_ORDER;
  const out: CascadeTemplateDef[] = [];
  for (let len = 2; len <= 5; len += 1) {
    for (const tuple of distinctChannelTuples(chs, len)) {
      const chain = channelChainLabel(tuple);
      const disabledReason = templateDisabledReason(tuple);
      out.push({
        key: tuple.join('>'),
        channels: tuple,
        title: chain,
        description: '',
        inDevelopment: Boolean(disabledReason),
        ...(disabledReason ? { disabledReason } : {}),
      });
    }
  }
  return out;
}

export function groupTemplatesByLength(defs: CascadeTemplateDef[]): { len: number; items: CascadeTemplateDef[] }[] {
  const map = new Map<number, CascadeTemplateDef[]>();
  for (const d of defs) {
    const n = d.channels.length;
    if (!map.has(n)) map.set(n, []);
    map.get(n)!.push(d);
  }
  const sortGroup = (items: CascadeTemplateDef[]) =>
    [...items].sort((a, b) => {
      if (a.inDevelopment !== b.inDevelopment) return a.inDevelopment ? 1 : -1;
      return a.key.localeCompare(b.key);
    });
  return [2, 3, 4, 5]
    .filter((len) => map.has(len))
    .map((len) => ({ len, items: sortGroup(map.get(len)!) }));
}
